import { useEffect, useRef, useState, type RefObject } from 'react'

import type { Preset } from '../gl/presets'
import { ReactionDiffusionEngine, type MaskMode } from '../gl/simulation'

export interface ReactionDiffusionBackgroundProps {
  preset: Preset
  /** paint chemical B under the pointer */
  interactive?: boolean
  className?: string
  /** URL of an image whose dark pixels drive the mask, centred */
  maskImage?: string
  /** mask width as a fraction of the shorter axis */
  maskCoverage?: number
  /** where the mark sits, 0..1 left-to-right */
  maskCenterX?: number
  /** where the mark sits, 0..1 top-to-bottom */
  maskCenterY?: number
  /** 'block' holds the shape clear of pattern; 'seed' grows out of it */
  maskMode?: MaskMode
  /** filled with the live engine, so callers can reset() or inspect it */
  engineRef?: RefObject<ReactionDiffusionEngine | null>
}

/** How far the pointer may travel between splats before the trail is filled in. */
const SPLAT_SPACING = 0.012

/**
 * Steps run before the field is first shown, so the page opens on a fully
 * developed pattern rather than on one visibly assembling itself. Spread
 * across frames by the engine and hidden behind a fade, so it costs a short
 * delay before the artwork appears rather than a frozen page.
 */
const WARMUP_STEPS = 7000

/**
 * Ceiling on how long that may take. A slow machine gets a less-developed
 * field rather than a frozen page.
 */
const WARMUP_BUDGET_MS = 950

export function ReactionDiffusionBackground({
  preset,
  interactive = true,
  className,
  maskImage,
  maskCoverage = 0.4,
  maskCenterX = 0.5,
  maskCenterY = 0.5,
  maskMode = 'block',
  engineRef: externalEngineRef,
}: ReactionDiffusionBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<ReactionDiffusionEngine | null>(null)
  const externalRef = useRef(externalEngineRef)
  externalRef.current = externalEngineRef
  const presetRef = useRef(preset)
  // Placement and size are re-applied by their own effects below, so they are
  // read through refs here — putting them in the setup effect's deps would
  // rebuild the engine and throw away the developed field on every resize.
  const maskCentreRef = useRef({ x: maskCenterX, y: maskCenterY })
  maskCentreRef.current = { x: maskCenterX, y: maskCenterY }
  const maskCoverageRef = useRef(maskCoverage)
  maskCoverageRef.current = maskCoverage
  const interactiveRef = useRef(interactive)
  const [supported, setSupported] = useState(true)
  const [ready, setReady] = useState(false)

  presetRef.current = preset
  interactiveRef.current = interactive

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let engine: ReactionDiffusionEngine
    try {
      engine = new ReactionDiffusionEngine(canvas, presetRef.current)
    } catch (error) {
      // Falls back to the CSS background painted behind the canvas.
      console.warn('reaction-diffusion background unavailable:', error)
      setSupported(false)
      return
    }
    engineRef.current = engine
    if (externalRef.current) externalRef.current.current = engine
    engine.setMaskCenter(maskCentreRef.current.x, maskCentreRef.current.y)

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    let reduced = motionQuery.matches

    const measure = () => {
      const rect = canvas.getBoundingClientRect()
      engine.resize(rect.width, rect.height, window.devicePixelRatio || 1)
    }

    const applyMotionPreference = () => {
      reduced = motionQuery.matches
      engine.setAnimating(!reduced)
      if (reduced) {
        engine.stop()
        // Still show the artwork, just held still.
        engine.renderOnce()
      } else if (!document.hidden) {
        engine.start()
      }
    }

    const begin = () => {
      // The loop runs during warm-up too — that is what spreads it across
      // frames — so start it regardless and settle the motion question once
      // the field is developed.
      engine.settle(WARMUP_STEPS, WARMUP_BUDGET_MS)
      setReady(true)
      applyMotionPreference()
    }

    const onVisibilityChange = () => {
      if (reduced) return
      if (document.hidden) engine.stop()
      else engine.start()
    }

    measure()

    // Seeding from an image is async. Hold off on the first frame until the
    // mask is in, otherwise the noise seed flashes up before it is replaced.
    let cancelled = false
    if (maskImage) {
      const image = new Image()
      image.decoding = 'async'
      image.src = maskImage
      image
        .decode()
        .then(() => {
          if (cancelled) return
          engine.setMaskImage(image, maskCoverageRef.current, maskMode)
          engine.reset()
          begin()
        })
        .catch((error: unknown) => {
          if (cancelled) return
          console.warn('mask image failed to load, continuing without it:', error)
          begin()
        })
    } else {
      begin()
    }

    const observer = new ResizeObserver(() => {
      measure()
      if (reduced) engine.renderOnce()
    })
    observer.observe(canvas)

    let lastPoint: { x: number; y: number } | null = null
    const onPointerMove = (event: PointerEvent) => {
      if (!interactiveRef.current || reduced) return
      // Touch is excluded on purpose. A finger is on the glass to scroll, not
      // to paint, so every swipe would drag a trail of B across the field —
      // and there is no hover state to make that feel intentional.
      if (event.pointerType === 'touch') return

      const rect = canvas.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return

      const x = (event.clientX - rect.left) / rect.width
      // Framebuffer UV origin is bottom-left; pointer coordinates are top-left.
      const y = 1 - (event.clientY - rect.top) / rect.height
      if (x < 0 || x > 1 || y < 0 || y > 1) {
        lastPoint = null
        return
      }

      if (lastPoint) {
        // Interpolate, or a fast pointer leaves a dotted line.
        const distance = Math.hypot(x - lastPoint.x, y - lastPoint.y)
        const steps = Math.min(6, Math.max(1, Math.ceil(distance / SPLAT_SPACING)))
        for (let i = 1; i <= steps; i++) {
          const t = i / steps
          engine.splat(lastPoint.x + (x - lastPoint.x) * t, lastPoint.y + (y - lastPoint.y) * t)
        }
      } else {
        engine.splat(x, y)
      }
      lastPoint = { x, y }
    }
    const onPointerLeave = () => {
      lastPoint = null
    }

    motionQuery.addEventListener('change', applyMotionPreference)
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pointermove', onPointerMove, { passive: true })
    window.addEventListener('pointerleave', onPointerLeave)

    return () => {
      cancelled = true
      observer.disconnect()
      motionQuery.removeEventListener('change', applyMotionPreference)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerleave', onPointerLeave)
      engine.dispose()
      engineRef.current = null
      if (externalRef.current) externalRef.current.current = null
    }
  }, [maskImage, maskMode])

  useEffect(() => {
    engineRef.current?.setPreset(preset)
  }, [preset])

  useEffect(() => {
    engineRef.current?.setMaskCenter(maskCenterX, maskCenterY)
  }, [maskCenterX, maskCenterY])

  useEffect(() => {
    engineRef.current?.setMaskCoverage(maskCoverage)
  }, [maskCoverage])

  if (!supported) return null

  return (
    <canvas
      ref={canvasRef}
      className={[className, ready ? 'is-ready' : null].filter(Boolean).join(' ')}
      aria-hidden="true"
    />
  )
}
